import { Card, Text } from "@repo/components";
import type { ReactNode } from "react";
import { css } from "@repo/styled-system/css";
import { Grid, HStack, Stack } from "@repo/styled-system/jsx";

/** Direction of a stat's change, driving the delta color. */
export type StatTrend = "up" | "down" | "neutral";

export interface Stat {
  /** Short metric name, e.g. "Monthly recurring revenue". */
  label: string;
  /** Pre-formatted primary value, e.g. "$48.2k" or "1,204". */
  value: ReactNode;
  /** Pre-formatted change indicator, e.g. "+12.4%" or "-3 pts". */
  delta?: string;
  /** Direction of the change. Defaults to "neutral" when a delta is shown. */
  trend?: StatTrend;
  /** Optional leading icon, e.g. a lucide-react icon element. */
  icon?: ReactNode;
}

export interface StatCardsProps {
  stats: Stat[];
  /** Minimum column width before wrapping. Defaults to 14rem. */
  minColumnWidth?: string;
}

const trendColor: Record<StatTrend, string> = {
  up: "green.fg",
  down: "red.fg",
  neutral: "fg.muted",
};

/**
 * StatCards — presentational row of KPI / stat cards.
 *
 * Each card shows a label, a primary value, an optional delta (colored by
 * trend) and an optional icon. All data is supplied via props; this block
 * holds no state and talks to no SDK.
 */
export function StatCards({ stats, minColumnWidth = "14rem" }: StatCardsProps) {
  return (
    <Grid
      bg="bg.canvas"
      gap="4"
      style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${minColumnWidth}, 1fr))` }}
    >
      {stats.map((stat, index) => {
        const trend: StatTrend = stat.trend ?? "neutral";
        return (
          <Card.Root key={index} bg="bg.default" borderColor="border.default" h="full">
            <Card.Body>
              <Stack gap="3">
                <HStack justify="space-between" gap="2">
                  <Text textStyle="sm" fontWeight="medium" color="fg.muted">
                    {stat.label}
                  </Text>
                  {stat.icon !== undefined && stat.icon !== null && (
                    <span
                      className={css({
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        w: "8",
                        h: "8",
                        rounded: "l2",
                        bg: "colorPalette.subtle",
                        // Readable accent on a subtle fill (`.fg` is on-solid near-white → washed out).
                        color: "colorPalette.text",
                        flexShrink: 0,
                      })}
                      aria-hidden="true"
                    >
                      {stat.icon}
                    </span>
                  )}
                </HStack>

                <HStack gap="2" alignItems="baseline">
                  <Text textStyle="3xl" fontWeight="bold" color="fg.default" lineHeight="1">
                    {stat.value}
                  </Text>
                  {stat.delta !== undefined && stat.delta !== null && (
                    <Text textStyle="sm" fontWeight="medium" color={trendColor[trend]}>
                      {stat.delta}
                    </Text>
                  )}
                </HStack>
              </Stack>
            </Card.Body>
          </Card.Root>
        );
      })}
    </Grid>
  );
}
